import React, { useEffect, useState } from 'react';

import {
  Col,
  Form,
  FormControl,
  FormLabel,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  uploadLargePackagePhoto,
  uploadMultiplePackagePhoto,
  confirmReceivedFromWarecloud,
} from './services';

import largePackages from '../../assets/img/large-packages.png';
import multiplePackages from '../../assets/img/multiple-packages.png';

import { setMessage } from '../toasts/toastSlice';
import { setOrdersModalConfig, setOrdersToRetrieveFilters } from './orderSlice';

import OrderCommentInput from '../common/_inputs/OrderComment';
import InputFile from '../common/_inputs/InputFile';
import OrderModalWrapper from './OrderModalWrapper';
import { useGetRatings } from '../../features/rating/services';
import { handleAxiosErrorMessage } from '../../utils';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_TYPES } from '../../config/consts/commons';
import { useQueryClient } from '@tanstack/react-query';

const OrdersToRetrieveModal = () => {
  const { t } = useTranslation(['system', 'description', 'button']);
  const queryClient = useQueryClient();
  const { ordersToRetrieveFilters, ordersModalConfig } = useSelector(
    (state) => state.orders
  );
  const { order } = ordersModalConfig;

  const [buttonLabel, setButtonLabel] = useState('confirm');

  const [comment, setComment] = useState('');

  const [temporalFile, setTemporalFile] = useState('');
  const [isFileCompressing, setIsFileCompressing] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);

  const [isLarge, setIsLarge] = useState(false);
  const [isMultiple, setIsMultiple] = useState(false);
  const [packageCount, setPackageCount] = useState(2);
  const [isSpecialPackage, setIsSpecialPackage] = useState(false);
  const [renderImageUpload, setRenderImageUpload] = useState(false);

  const [enabled, setEnabled] = useState(false);
  useGetRatings(enabled);

  useEffect(() => {
    setIsSpecialPackage(isLarge || isMultiple);
  }, [isLarge, isMultiple]);

  const options = {
    1: t('orders.retrieve.modal.picking_large_order', { ns: 'description' }),
    2: t('orders.retrieve.modal.picking_order_more_package', {
      ns: 'description',
    }),
    3: t('orders.retrieve.modal.picking_normal_order', { ns: 'description' }),
  };

  const imageCopywrite = (isLarge, isMultiple) => (
    <Row>
      <Col className="text-center">
        <p>
          {isLarge
            ? t('orders.retrieve.modal.image_copyright_large', {
                ns: 'description',
              })
            : t('orders.retrieve.modal.image_copyright', { ns: 'description' })}
        </p>
        <img
          width="300px"
          className="m-1"
          src={isLarge ? largePackages : multiplePackages}
          alt=""
        />
      </Col>
    </Row>
  );

  const dispatch = useDispatch();

  const submitOrderRetrieval = async ({ specialPackagePayload }) => {
    const {
      id_order,
      order_code,
      id_order_transfer_confirmation,
      qr_code = false,
    } = order;
    if (!isSpecialPackage) setIsSubmitting(true);
    try {
      const payload = {
        id_order,
        comment,
        id_order_transfer_confirmation:
          id_order_transfer_confirmation ?? undefined,
        qr_code,
        ...specialPackagePayload,
      };

      await confirmReceivedFromWarecloud(payload);

      resetModal();
      dispatch(setOrdersToRetrieveFilters({ ...ordersToRetrieveFilters }));

      dispatch(
        setMessage({
          body: t('ORDER_WITHDRAW_SUCCESS', {
            ns: 'system',
            order: order_code,
          }),
          type: NOTIFICATION_TYPES.SUCCESS,
        })
      );

      setEnabled(true);
    } catch (err) {
      const errorMessage = handleAxiosErrorMessage(err);
      setIsSubmitting(false);
      setButtonLabel('confirm_return');
      setShowSubmitError(true);

      dispatch(
        setMessage({
          body: t(errorMessage, { ns: 'system' }),
          type: 'error',
        })
      );
    }
  };

  const buildSpecialPackagePayload = async () => {
    let specialPackagePayload = {};
    const { id_order } = order;
    let photosArray = [];
    setIsSubmitting(true);
    try {
      if (isLarge) {
        const formData = new FormData();
        formData.append('file', temporalFile);
        const uploadResponse = await uploadLargePackagePhoto({
          id_order,
          formData,
        });
        photosArray.push(uploadResponse.data);
        specialPackagePayload = {
          ...specialPackagePayload,
          is_large: true,
          photo_links: photosArray,
        };
      }
      if (isMultiple) {
        const formData = new FormData();
        formData.append('file', temporalFile);
        const uploadResponse = await uploadMultiplePackagePhoto({
          id_order,
          formData,
        });
        photosArray.push(uploadResponse.data);
        specialPackagePayload = {
          ...specialPackagePayload,
          package_count: packageCount,
          photo_links: photosArray,
        };
      }
      return specialPackagePayload;
    } catch (err) {
      const errorMessage = handleAxiosErrorMessage(err);
      setIsSubmitting(false);
      setButtonLabel('confirm_return');
      setShowSubmitError(true);
      dispatch(
        setMessage({
          body: t(errorMessage, { ns: 'system' }),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
    }
  };

  const handleConfirmation = async () => {
    setShowSubmitError(false);
    if (isSpecialPackage) {
      if (!renderImageUpload) {
        setRenderImageUpload(true);
      } else {
        setIsSubmitting(true);
        const specialPackagePayload = await buildSpecialPackagePayload();
        submitOrderRetrieval({ specialPackagePayload });
      }
    } else {
      submitOrderRetrieval({});
    }
  };

  const resetModal = () => {
    const { show, ...rest } = ordersModalConfig;
    setComment('');
    setButtonLabel('confirm');
    setIsSubmitting(false);
    setShowSubmitError(false);
    dispatch(setOrdersModalConfig({ ...rest, show: false }));
    queryClient.removeQueries({
      queryKey: ['validateOrderByQR', { idOrder: undefined }],
    });
  };

  return (
    <OrderModalWrapper
      handleClose={resetModal}
      handleSubmit={handleConfirmation}
      buttonLabel={t(buttonLabel, { ns: 'button' })}
      isSubmitting={isSubmitting}
      showSubmitError={showSubmitError}
      submitButtonDisabled={
        (renderImageUpload && !temporalFile) || isFileCompressing
      }
    >
      {renderImageUpload ? (
        <Row className="align-items-center justify-content-center ">
          <Col>
            <InputFile
              label={imageCopywrite(isLarge, isMultiple)}
              onFileReady={setTemporalFile}
              onProcessingChange={setIsFileCompressing}
              inputId="file-retrieve-order"
            />
            {isMultiple && (
              <InputGroup className="mb-3" size="sm">
                <FormLabel className="me-2 my-0">
                  {t('orders.retrieve.modal.number_package_order', {
                    ns: 'description',
                  })}
                </FormLabel>
                <FormControl
                  type="number"
                  min="2"
                  aria-label="Amount in units"
                  placeholder="2"
                  value={packageCount}
                  onChange={(e) => setPackageCount(e.target.value)}
                  onBlur={(e) => setPackageCount(Math.max(2, e.target.value))}
                />
              </InputGroup>
            )}
          </Col>
        </Row>
      ) : (
        <>
          <span>
            {t('orders.retrieve.modal.characteristics_order', {
              ns: 'description',
            })}
          </span>
          <Form.Check
            id="3"
            type="radio"
            label={options[3]}
            onChange={(e) => {
              setIsMultiple(false);
              setIsLarge(false);
            }}
            checked={!isSpecialPackage}
          />
          <Form.Check
            id="1"
            type="radio"
            label={options[1]}
            onChange={(e) => {
              setIsLarge(e.target.checked);
              setIsMultiple(false);
            }}
            checked={isLarge}
          />
          <Form.Check
            id="2"
            type="radio"
            label={options[2]}
            onChange={(e) => {
              setIsMultiple(e.target.checked);
              setIsLarge(false);
            }}
            checked={isMultiple}
          />

          <OrderCommentInput setComment={(payload) => setComment(payload)} />
        </>
      )}
    </OrderModalWrapper>
  );
};

export default OrdersToRetrieveModal;
